import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { 
  Building2, Users, Crown, DoorOpen, FileText, CreditCard, 
  MessageSquare, BarChart3, CheckCircle, ArrowRight, ArrowLeft,
  Sparkles, Home, Receipt, Bell, Upload, Lock, Archive,
  Wifi, Droplets, Zap, Shield, ScrollText, Camera
} from 'lucide-react';

const ONBOARDING_KEY = 'udorm_onboarding_v2';

// Onboarding steps for each role
const ADMIN_STEPS = [
  {
    title: "Welcome to U-DORM Admin! 🎉",
    description: "As an administrator, you manage the entire platform. Let's walk through your key responsibilities.",
    icon: Sparkles,
    color: "bg-indigo-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>You have full control over:</p>
        <ul className="space-y-2 ml-4">
          <li className="flex items-center gap-2"><Users className="w-4 h-4 text-indigo-500" /> Managing landlord accounts</li>
          <li className="flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" /> Subscription management</li>
          <li className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-emerald-500" /> Platform analytics</li>
        </ul>
      </div>
    )
  },
  {
    title: "Managing Landlords",
    description: "View and manage all landlord accounts on your platform.",
    icon: Users,
    color: "bg-blue-500",
    content: (
      <div className="space-y-3 text-sm">
        <p><strong>Landlords</strong> section allows you to:</p>
        <ul className="space-y-2 ml-4 list-disc">
          <li>View all registered landlords and their account status</li>
          <li>See property, unit, and tenant counts at a glance</li>
          <li>Monitor subscription status (active / expired / lapsed)</li>
          <li>Invite a new landlord via secure email link</li>
        </ul>
      </div>
    )
  },
  {
    title: "Subscriptions",
    description: "Track Landlord subscriptions powered by Stripe.",
    icon: Crown,
    color: "bg-amber-500",
    content: (
      <div className="space-y-3 text-sm">
        <p><strong>U-DORM has one plan — billed two ways:</strong></p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="p-3 bg-muted rounded text-center">
            <p className="font-semibold">Monthly</p>
            <p className="text-xs text-muted-foreground">$9.99 / month</p>
          </div>
          <div className="p-3 bg-muted rounded text-center border border-[#C85A42]/40">
            <p className="font-semibold text-[#C85A42]">Annual</p>
            <p className="text-xs text-muted-foreground">$95 / year · save ~20%</p>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Both plans include everything (unlimited properties/units/tenants) with a 30-day money-back guarantee. No auto-renew — landlords renew on their own. Annual subscribers get +36 bonus days on their 2nd consecutive annual renewal.
        </p>
      </div>
    )
  },
  {
    title: "You're All Set! ✅",
    description: "You're ready to manage your platform.",
    icon: CheckCircle,
    color: "bg-emerald-500",
    content: (
      <div className="space-y-3 text-sm text-center">
        <p>Head to your <strong>Dashboard</strong> to see platform statistics and start managing landlords.</p>
        <p className="text-muted-foreground">You can always access help from the sidebar.</p>
      </div>
    )
  }
];

const LANDLORD_STEPS = [
  {
    title: "Welcome to U-DORM! 🏠",
    description: "Let's get you set up to manage your dormitory properties efficiently.",
    icon: Sparkles,
    color: "bg-[#C85A42]",
    content: (
      <div className="space-y-3 text-sm">
        <p>As a landlord, you can:</p>
        <ul className="space-y-2 ml-4">
          <li className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#C85A42]" /> Manage properties & units</li>
          <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-blue-500" /> Create lease agreements</li>
          <li className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-emerald-500" /> Track & verify payments</li>
          <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-purple-500" /> Communicate with tenants</li>
        </ul>
      </div>
    )
  },
  {
    title: "Step 1: Add Your Property",
    description: "Start by adding your dormitory building.",
    icon: Building2,
    color: "bg-[#C85A42]",
    content: (
      <div className="space-y-3 text-sm">
        <p>Go to <strong>Properties</strong> → Click <strong>"Add Property"</strong></p>
        <div className="p-3 bg-muted rounded-lg">
          <p className="font-medium mb-2">Enter property details:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Property name (e.g., "Sunrise Dormitory")</li>
            <li>• Full address</li>
            <li>• Description (optional)</li>
            <li>• Property image URL (optional)</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    title: "Step 2: Create Units/Rooms",
    description: "Add individual rooms to your property.",
    icon: DoorOpen,
    color: "bg-[#6B8E6B]",
    content: (
      <div className="space-y-3 text-sm">
        <p>Go to <strong>Units</strong> → Click <strong>"Add Unit"</strong></p>
        <div className="p-3 bg-muted rounded-lg">
          <p className="font-medium mb-2">For each room, specify:</p>
          <ul className="space-y-1 text-muted-foreground">
            <li>• Unit/Room number</li>
            <li>• Floor location</li>
            <li>• Capacity (number of occupants)</li>
            <li>• Monthly rent amount</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    title: "Step 3: Create a Lease",
    description: "Set up lease agreements with all terms and fees.",
    icon: FileText,
    color: "bg-blue-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>Go to <strong>Leases</strong> → Click <strong>"Create Lease"</strong></p>
        <div className="p-3 bg-muted rounded-lg space-y-2">
          <p className="font-medium">Lease includes:</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="flex items-center gap-1"><Wifi className="w-3 h-3 text-blue-500" /> Internet fee</div>
            <div className="flex items-center gap-1"><Droplets className="w-3 h-3 text-cyan-500" /> Water fee</div>
            <div className="flex items-center gap-1"><Zap className="w-3 h-3 text-yellow-500" /> Electricity fee</div>
            <div className="flex items-center gap-1"><Shield className="w-3 h-3 text-purple-500" /> House rules</div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Total rent = Base rent + All additional fees
          </p>
        </div>
      </div>
    )
  },
  {
    title: "The Lease Workflow",
    description: "Understanding the lease approval process.",
    icon: Lock,
    color: "bg-amber-500",
    content: (
      <div className="space-y-3 text-sm">
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded">
            <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">1</span>
            <span>You create lease → <strong>Pending</strong></span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
            <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">2</span>
            <span>Tenant acknowledges → <strong>Locked</strong></span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded">
            <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center">3</span>
            <span>You activate → <strong>Active</strong></span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          <Lock className="w-3 h-3 inline mr-1" />
          Once acknowledged, lease is locked. Use "Issue New" for changes.
        </p>
      </div>
    )
  },
  {
    title: "Managing Payments",
    description: "Review and verify tenant payment submissions.",
    icon: CreditCard,
    color: "bg-emerald-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>Tenants submit payments with proof (screenshots).</p>
        <div className="p-3 bg-muted rounded-lg">
          <p className="font-medium mb-2">Your actions:</p>
          <ul className="space-y-1">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span><strong>Verify</strong> - Confirm payment received</span>
            </li>
            <li className="flex items-center gap-2">
              <Archive className="w-4 h-4 text-red-500" />
              <span><strong>Reject</strong> - If proof is invalid</span>
            </li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">
            Verified payments auto-generate receipts for tenants.
          </p>
        </div>
      </div>
    )
  },
  {
    title: "You're Ready! 🎉",
    description: "Start managing your properties now.",
    icon: CheckCircle,
    color: "bg-emerald-500",
    content: (
      <div className="space-y-4 text-sm text-center">
        <p>Quick start checklist:</p>
        <div className="space-y-2 text-left max-w-xs mx-auto">
          <div className="flex items-center gap-2 p-2 bg-muted rounded">
            <div className="w-5 h-5 rounded border-2 border-[#C85A42]" />
            <span>Add your first property</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-muted rounded">
            <div className="w-5 h-5 rounded border-2 border-[#6B8E6B]" />
            <span>Create rooms/units</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-muted rounded">
            <div className="w-5 h-5 rounded border-2 border-blue-500" />
            <span>Set up your first lease</span>
          </div>
        </div>
      </div>
    )
  }
];

const MANAGER_STEPS = [
  {
    title: "Welcome, Property Manager! 🏢",
    description: "You're helping landlords manage their properties efficiently.",
    icon: Sparkles,
    color: "bg-[#5B7B7B]",
    content: (
      <div className="space-y-3 text-sm">
        <p>As a property manager, you can:</p>
        <ul className="space-y-2 ml-4">
          <li className="flex items-center gap-2"><Building2 className="w-4 h-4 text-blue-500" /> Manage assigned properties</li>
          <li className="flex items-center gap-2"><DoorOpen className="w-4 h-4 text-purple-500" /> Handle unit management</li>
          <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-emerald-500" /> View & manage leases</li>
          <li className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#C85A42]" /> Process payments</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2">
          Your access is based on permissions set by your landlord.
        </p>
      </div>
    )
  },
  {
    title: "Your Permissions",
    description: "Understanding what you can do in the system.",
    icon: Shield,
    color: "bg-amber-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>Your landlord has assigned specific permissions:</p>
        <div className="grid grid-cols-2 gap-2 mt-2">
          <div className="p-2 bg-muted rounded text-xs">
            <Building2 className="w-4 h-4 mb-1 text-blue-500" />
            Properties & Units
          </div>
          <div className="p-2 bg-muted rounded text-xs">
            <FileText className="w-4 h-4 mb-1 text-emerald-500" />
            Leases
          </div>
          <div className="p-2 bg-muted rounded text-xs">
            <CreditCard className="w-4 h-4 mb-1 text-purple-500" />
            Payments
          </div>
          <div className="p-2 bg-muted rounded text-xs">
            <Users className="w-4 h-4 mb-1 text-[#C85A42]" />
            Tenants
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Check your Dashboard to see your active permissions.
        </p>
      </div>
    )
  },
  {
    title: "Managing Properties",
    description: "View and manage properties assigned to you.",
    icon: Building2,
    color: "bg-blue-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>Go to <strong>Properties</strong> to see your assigned properties.</p>
        <div className="p-3 bg-muted rounded-lg space-y-2">
          <p className="font-medium">You can:</p>
          <ul className="space-y-1 text-sm">
            <li>• View property details & addresses</li>
            <li>• See unit counts & occupancy</li>
            <li>• Manage units if permitted</li>
            <li>• View associated leases</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    title: "Processing Payments",
    description: "Help verify tenant payments.",
    icon: CreditCard,
    color: "bg-emerald-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>If you have payment permissions:</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded">
            <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">1</span>
            <span>View pending payments</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
            <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">2</span>
            <span>Review payment proof</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded">
            <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center">3</span>
            <span>Verify or request resubmission</span>
          </div>
        </div>
      </div>
    )
  },
  {
    title: "Communication",
    description: "Stay connected with tenants and landlords.",
    icon: MessageSquare,
    color: "bg-[#C85A42]",
    content: (
      <div className="space-y-3 text-sm">
        <p>Use the <strong>Messages</strong> feature to:</p>
        <ul className="space-y-2 ml-4">
          <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-blue-500" /> Communicate with tenants</li>
          <li className="flex items-center gap-2"><Bell className="w-4 h-4 text-amber-500" /> Receive notifications</li>
          <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-emerald-500" /> Track inquiries</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2">
          Check your <strong>Notifications</strong> regularly for updates!
        </p>
      </div>
    )
  },
  {
    title: "You're All Set! 🎉",
    description: "Start managing your assigned properties.",
    icon: CheckCircle,
    color: "bg-emerald-500",
    content: (
      <div className="space-y-3 text-sm text-center">
        <div className="w-16 h-16 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
          <CheckCircle className="w-8 h-8 text-emerald-500" />
        </div>
        <p>You're ready to help manage properties!</p>
        <div className="p-3 bg-muted rounded-lg">
          <p className="font-medium mb-2">Quick Tips:</p>
          <ul className="text-xs space-y-1 text-left">
            <li>📊 Check Dashboard for overview</li>
            <li>🔔 Monitor Notifications</li>
            <li>💬 Respond to tenant messages</li>
            <li>❓ Use Help & FAQs if needed</li>
          </ul>
        </div>
      </div>
    )
  }
];

const TENANT_STEPS = [
  {
    title: "Welcome to U-DORM! 🏠",
    description: "Your portal for managing your rental experience.",
    icon: Sparkles,
    color: "bg-[#6B8E6B]",
    content: (
      <div className="space-y-3 text-sm">
        <p>As a tenant, you can:</p>
        <ul className="space-y-2 ml-4">
          <li className="flex items-center gap-2"><FileText className="w-4 h-4 text-blue-500" /> View & acknowledge your lease</li>
          <li className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-emerald-500" /> Submit rent payments</li>
          <li className="flex items-center gap-2"><Receipt className="w-4 h-4 text-purple-500" /> Download official receipts</li>
          <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#C85A42]" /> Message your landlord</li>
        </ul>
      </div>
    )
  },
  {
    title: "Your Lease Agreement",
    description: "Review and acknowledge your rental terms.",
    icon: FileText,
    color: "bg-blue-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>When your landlord creates a lease for you:</p>
        <div className="space-y-2">
          <div className="flex items-center gap-2 p-2 bg-amber-50 rounded">
            <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs flex items-center justify-center">1</span>
            <span>Review all terms carefully</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-blue-50 rounded">
            <span className="w-6 h-6 rounded-full bg-blue-500 text-white text-xs flex items-center justify-center">2</span>
            <span>Check rent breakdown & fees</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded">
            <span className="w-6 h-6 rounded-full bg-emerald-500 text-white text-xs flex items-center justify-center">3</span>
            <span>Click "Acknowledge" to confirm</span>
          </div>
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Lock className="w-3 h-3" /> Once you acknowledge, the lease terms are locked but always viewable
        </p>
      </div>
    )
  },
  {
    title: "Submitting Payments",
    description: "Pay your rent and upload proof of payment.",
    icon: Upload,
    color: "bg-emerald-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>Go to <strong>Payments</strong> → Click <strong>"Submit Payment"</strong></p>
        <div className="p-3 bg-muted rounded-lg space-y-2">
          <p className="font-medium">Payment methods accepted:</p>
          <p className="text-xs text-muted-foreground">
            GCash, Maya, Bank transfer, Cash — and any other method your landlord accepts. Your landlord will share their accepted payment methods with you.
          </p>
          <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5">
            <Camera className="w-3.5 h-3.5" />
            Upload a screenshot of your payment as proof
          </p>
        </div>
      </div>
    )
  },
  {
    title: "Getting Receipts",
    description: "Download official receipts after payment verification.",
    icon: Receipt,
    color: "bg-purple-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>After your landlord verifies your payment:</p>
        <div className="p-3 bg-muted rounded-lg">
          <ul className="space-y-2">
            <li className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              <span>Payment status changes to <strong>Verified</strong></span>
            </li>
            <li className="flex items-center gap-2">
              <Receipt className="w-4 h-4 text-purple-500" />
              <span>Official receipt is generated</span>
            </li>
            <li className="flex items-center gap-2">
              <ArrowRight className="w-4 h-4 text-blue-500" />
              <span>Download PDF from <strong>Receipts</strong> page</span>
            </li>
          </ul>
        </div>
      </div>
    )
  },
  {
    title: "Messaging Your Landlord",
    description: "Stay in touch directly in-app.",
    icon: MessageSquare,
    color: "bg-cyan-500",
    content: (
      <div className="space-y-3 text-sm">
        <p>Go to <strong>Messages</strong> in the sidebar to chat with your landlord (or property manager) without leaving U-DORM.</p>
        <ul className="space-y-2 ml-4 list-disc text-xs text-muted-foreground">
          <li>Ask about maintenance, rent due dates, lease questions</li>
          <li>Share photos directly in chat (e.g., a leak, repair needed)</li>
          <li>Messages are kept on your account — easy to look back later</li>
        </ul>
      </div>
    )
  },
  {
    title: "You're All Set! 🎉",
    description: "Enjoy your stay!",
    icon: CheckCircle,
    color: "bg-emerald-500",
    content: (
      <div className="space-y-4 text-sm text-center">
        <p>Quick tips:</p>
        <div className="space-y-2 text-left max-w-xs mx-auto">
          <div className="flex items-center gap-2 p-2 bg-muted rounded">
            <Bell className="w-4 h-4 text-amber-500" />
            <span>Pay rent by the due date</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-muted rounded">
            <MessageSquare className="w-4 h-4 text-blue-500" />
            <span>Use Messages for any concerns</span>
          </div>
          <div className="flex items-center gap-2 p-2 bg-muted rounded">
            <Receipt className="w-4 h-4 text-purple-500" />
            <span>Keep receipts for your records</span>
          </div>
        </div>
      </div>
    )
  }
];

export function OnboardingWizard({ externalOpen, onExternalClose }) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  // Handle first-time user auto-open
  // NOTE: Auto-popup has been disabled per product decision — the role tour now opens
  // only when the user explicitly clicks "Help & Tutorial" from the sidebar (externalOpen).
  useEffect(() => {
    // Intentionally left blank — no auto-popup on first login for the role tour.
    // The Landlord onboarding (LandlordOnboarding) handles first-login experience for landlords.
  }, [user]);

  // Handle external trigger (from Help button)
  useEffect(() => {
    if (externalOpen) {
      setCurrentStep(0);
      setIsOpen(true);
    }
  }, [externalOpen]);

  const handleOpenChange = (open) => {
    setIsOpen(open);
    if (!open) {
      // Always mark as shown when closing, regardless of method (X, Skip, Complete)
      if (user) {
        const completedKey = `${ONBOARDING_KEY}_${user.user_id}`;
        localStorage.setItem(completedKey, 'true');
      }
      if (onExternalClose) {
        onExternalClose();
      }
    }
  };

  const getSteps = () => {
    if (!user) return [];
    switch (user.role) {
      case 'admin': return ADMIN_STEPS;
      case 'landlord': return LANDLORD_STEPS;
      case 'property_manager': return MANAGER_STEPS;
      case 'tenant': return TENANT_STEPS;
      default: return [];
    }
  };

  const steps = getSteps();
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Already marked as shown in useEffect, just close
    setIsOpen(false);
    setDontShowAgain(false);
    if (onExternalClose) {
      onExternalClose();
    }
  };

  const handleSkip = () => {
    // Already marked as shown in useEffect, just close
    setIsOpen(false);
    setDontShowAgain(false);
    if (onExternalClose) {
      onExternalClose();
    }
  };

  if (!user || steps.length === 0) return null;

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="onboarding-wizard">
        {/* Header */}
        <div className={`${currentStepData.color} p-6 text-white`}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
              <Icon className="w-6 h-6" />
            </div>
            <span className="text-sm opacity-80">
              Step {currentStep + 1} of {steps.length}
            </span>
          </div>
          <h2 className="text-xl font-bold">{currentStepData.title}</h2>
          <p className="text-sm opacity-90 mt-1">{currentStepData.description}</p>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4">
          <Progress value={progress} className="h-1" />
        </div>

        {/* Content */}
        <div className="p-6 pt-4">
          {currentStepData.content}
        </div>

        {/* Footer */}
        <div className="p-6 pt-0 space-y-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="dont-show-again"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
              data-testid="dont-show-again-checkbox"
            />
            <label htmlFor="dont-show-again" className="text-sm text-muted-foreground cursor-pointer">
              Don't show this tutorial again
            </label>
          </div>
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={handleSkip}
              className="text-muted-foreground"
              data-testid="onboarding-skip-btn"
            >
              Skip Tutorial
            </Button>
            <div className="flex gap-2">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  data-testid="onboarding-prev-btn"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </Button>
              )}
              <Button
                onClick={handleNext}
                className={currentStepData.color.replace('bg-', 'bg-') + ' hover:opacity-90'}
                data-testid="onboarding-next-btn"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    Get Started
                    <CheckCircle className="w-4 h-4 ml-1" />
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Hook to manually trigger onboarding
export function useOnboarding() {
  const { user } = useAuth();

  const resetOnboarding = () => {
    if (user) {
      localStorage.removeItem(`${ONBOARDING_KEY}_${user.user_id}`);
      window.location.reload();
    }
  };

  const isCompleted = () => {
    if (!user) return false;
    return localStorage.getItem(`${ONBOARDING_KEY}_${user.user_id}`) === 'true';
  };

  return { resetOnboarding, isCompleted };
}
